<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-center justify-between gap-4 flex-wrap w-full">
        <div class="flex items-baseline gap-2 flex-wrap">
          <h2 class="text-base font-bold text-[#0d4169] text-left">
            {{ resolvedCompanyName }}
          </h2>
          <span class="text-sm font-bold text-slate-600">Monthly Configuration</span>
        </div>

        <div class="flex items-center gap-6 shrink-0">
          <button
            type="button"
            :disabled="!canNavigateToAttendance"
            @click="$emit('navigate', 'attendance')"
            :class="activeTab === 'attendance' ? 'border-[#0d4169] text-[#0d4169]' : 'border-transparent text-slate-500'"
            class="pb-1.5 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors tracking-wide"
          >
            Attendance
          </button>
          <button
            type="button"
            :disabled="!canNavigateToEarn"
            @click="$emit('navigate', 'earn')"
            :class="activeTab === 'earn' ? 'border-[#0d4169] text-[#0d4169]' : 'border-transparent text-slate-500'"
            class="pb-1.5 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors tracking-wide"
          >
            Earn And Deduct
          </button>
        </div>
      </div>
    </div>

    <slot />
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  activeTab: {
    type: String,
    required: true,
    validator: (v) => v === 'earn' || v === 'attendance'
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  },
  monthlyPrivilegeName: {
    type: String,
    default: 'Monthly Configuration'
  },
  earnPrivilegeName: {
    type: String,
    default: 'Earn And Deduct'
  },
  attendancePrivilegeName: {
    type: String,
    default: 'Attendance'
  }
})

defineEmits(['navigate'])

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function hasAnyPrivilege(name) {
  const entry = getPrivilegeEntry(name)
  if (!entry || typeof entry !== 'object') return false
  return Object.values(entry).some((value) => value === true)
}

const canNavigateToEarn = computed(() => {
  if (props.skipPrivilegeCheck || hasAllPrivilege.value) return true
  return hasAnyPrivilege(props.monthlyPrivilegeName) || hasAnyPrivilege(props.earnPrivilegeName)
})

const canNavigateToAttendance = computed(() => {
  if (props.skipPrivilegeCheck || hasAllPrivilege.value) return true
  return hasAnyPrivilege(props.monthlyPrivilegeName) || hasAnyPrivilege(props.attendancePrivilegeName)
})

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})
</script>
